import {
  DarwinFlex,
  DMenuProps,
  DTableProps,
  StatusChip,
  StatusChipColors,
  renderUserCell,
  renderDateCell,
  ActionsDropdown,
  CombinedText,
  STATUS_COL_WIDTH,
  ACTIONS_COL_WIDTH,
} from '@darwin-ui';
import { ProcessMapTable } from 'types/processesTypes';
import Icon from 'components/darwin-ui/Icon';
import { UserList } from 'types/userTypes';
import { TFunction } from 'i18next';
import { ColumnAction } from './ProcessMapList';
import {
  ProcessLifeCycleStatus,
  getProcessStatusColor,
  getProcessStatusLabel,
} from 'constants/process-lifecycle-status.constants';

export const getProcessMapsColumns = (
  t: TFunction,
  actions: ColumnAction[],
  searchQuery: string,
  isArchivedView: boolean,
  onArchive?: ColumnAction['onClick'],
  onUnarchive?: ColumnAction['onClick']
) => {
  let allActions = [...actions];

  if (!isArchivedView && onArchive) {
    allActions.push({
      key: 'archive',
      name: 'archive',
      label: t('list-view.action-items.archive'),
      onClick: onArchive,
    });
  }

  if (isArchivedView && onUnarchive) {
    allActions.push({
      key: 'unarchive',
      name: 'archive',
      label: t('list-view.action-items.unarchive'),
      onClick: onUnarchive,
    });
  }

  const menuItems = (processMap: ProcessMapTable): DMenuProps['items'] =>
    allActions.map(action => ({
      key: action.key,
      label:
        action.key === 'activate-deactivate' ? (
          <div onClick={() => action?.onClick?.(processMap)}>
            <StatusChip
              label={
                processMap.active
                  ? t('process-map.columns.deactivate')
                  : t('process-map.columns.activate')
              }
              color={
                processMap.active
                  ? StatusChipColors.red
                  : StatusChipColors.green
              }
            />
          </div>
        ) : (
          <DarwinFlex
            gap={8}
            align="center"
            onClick={() => action?.onClick?.(processMap)}
          >
            <Icon
              name={action.name ? action.name : 'darwin-user'}
              size="sm"
              color="inherit"
            />
            {action.label}
          </DarwinFlex>
        ),
    }));

  const renderUserCol = (user: string) => renderUserCell(user, searchQuery);

  const columns: DTableProps<ProcessMapTable>['columns'] = [
    {
      title: t('process-map.columns.process-map-name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string) => (
        <CombinedText lines={1} text={name} highlight={searchQuery} />
      ),
      width: 240,
    },
    {
      title: t('process-map.columns.description'),
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      render: (description: string) => (
        <CombinedText lines={1} text={description} highlight={searchQuery} />
      ),
    },
    {
      title: t('process-map.columns.associated-products'),
      dataIndex: 'productsCount',
      key: 'productsCount',
      sorter: true,
      align: 'center',
      render: (productsCount: number) => productsCount,
      width: 200,
    },
    {
      title: t('process-map.columns.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      align: 'center',
      render: (status: string) => {
        return (
          <StatusChip
            color={getProcessStatusColor(status as ProcessLifeCycleStatus)}
            label={getProcessStatusLabel(status as ProcessLifeCycleStatus, t)}
          />
        );
      },
      width: STATUS_COL_WIDTH,
    },
    {
      title: t('process-map.columns.created-by'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: true,
      render: (record: UserList) => {
        if (!record || record.firstName == null) return renderUserCol('');
        const name = `${record.firstName || ''} ${record.lastName || ''}`;
        return renderUserCol(name);
      },
    },
    {
      title: t('process-map.columns.created-date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: renderDateCell,
      align: 'center',
    },
    {
      title: t('process-map.columns.modified-by'),
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      sorter: true,
      render: (record: UserList) => {
        if (!record || record.firstName == null) return renderUserCol('');
        const name = `${record.firstName || ''} ${record.lastName || ''}`;
        return renderUserCol(name);
      },
    },
    {
      title: t('process-map.columns.modified-date'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      render: renderDateCell,
      align: 'center',
      width: 200,
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_: unknown, process: ProcessMapTable) => (
        <ActionsDropdown item={process} getMenuItems={menuItems} />
      ),
      width: ACTIONS_COL_WIDTH,
    },
  ];

  return columns;
};
